import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Users, FileCheck, Shield, ClipboardCheck, ClipboardList } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OHSHome() {
  const navigate = useNavigate();
  const [openActions, setOpenActions] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem("token");
    fetch(`${API}/toolbox-talks/corrective-actions/open-count`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setOpenActions(d.open_count || 0))
      .catch(() => {});
  }, []);

  const ohsModules = [
    {
      title: "Daily Site Attendance Tracking",
      description: "Monitor personnel check-ins and site attendance compliance",
      icon: Users,
      path: "/ohs/attendance",
      color: "from-green-500 to-emerald-600",
    },
    {
      title: "Site Induction Records",
      description: "Manage and track site safety inductions for all personnel",
      icon: FileCheck,
      path: "/ohs/inductions",
      color: "from-blue-500 to-indigo-600",
    },
    {
      title: "Safety Walks",
      description: "Conduct safety inspections and track hazard closeout actions",
      icon: ClipboardCheck,
      path: "/ohs/safety-walks",
      color: "from-orange-500 to-red-600",
    },
    {
      title: "Toolbox Talks",
      description: "Record safety briefings — who was briefed, what was discussed, and what came of it",
      icon: ClipboardList,
      path: "/ohs/toolbox-talks",
      color: "from-purple-500 to-fuchsia-600",
    },
  ];

  return (
    <div className="min-h-screen p-6 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <Shield className="w-10 h-10 text-blue-600" />
            <h1 className="text-4xl font-bold text-slate-800">OH&S Management</h1>
          </div>
          <p className="text-lg text-slate-600">
            Occupational Health & Safety - Manage site safety compliance and personnel tracking
          </p>
        </div>

        {/* Module Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {ohsModules.map((module) => {
            const Icon = module.icon;
            return (
              <Card
                key={module.path}
                className="cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-2 hover:border-blue-400"
                onClick={() => navigate(module.path)}
              >
                <CardHeader>
                  <div className={`w-16 h-16 rounded-lg bg-gradient-to-br ${module.color} flex items-center justify-center mb-4 shadow-lg`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-2xl text-slate-800">{module.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600">{module.description}</p>
                  {module.path === "/ohs/toolbox-talks" && openActions > 0 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate("/ohs/toolbox-talks?filter=open"); }}
                      className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-red-100 text-red-700 px-3 py-1 text-sm font-medium hover:bg-red-200 transition-colors cursor-pointer"
                      data-testid="ohs-open-actions-badge">
                      <span className="w-2 h-2 rounded-full bg-red-500" />
                      {openActions} open corrective action{openActions === 1 ? "" : "s"} →
                    </button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Info Box */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <Shield className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">About OH&S Management</h3>
              <p className="text-sm text-blue-800">
                The OH&S Management system helps you maintain safety compliance across all sites by tracking personnel 
                attendance and managing safety inductions. Ensure all workers complete required safety training before 
                accessing sites and monitor daily attendance for compliance reporting.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
